<template>
  <v-card
    :class="{
      'gray-background-widget': isGrayOut,
      'widget-fullscreen': isFullscreen,
      widget: !isFullscreen,
      'widget-disconnected-device-theme': enableDisconnectedDeviceTheme
    }"
  >
    <v-card-item :class="cardTitleClasses">
      <v-card-title>
        <span v-if="hasActions || !isDialog" class="widget-actions">
          <v-menu>
            <template v-slot:activator="{ props }">
              <v-btn
                v-bind="props"
                class="menu-dropdown text-black"
                variant="text"
                data-cy="dashboardWidgetDropdown"
              >
                <v-icon icon="mdi-dots-vertical" />
              </v-btn>
            </template>
            <v-list>
              <slot name="actions"></slot>
              <!-- Configure -->
              <v-list-item
                v-if="!isDialog"
                @click.stop="dialogs.configure = true"
                prepend-icon="mdi-cog"
                :title="$t('widget.menu.configure')"
                data-cy="widgetMenuConfigure"
              />
              <!-- Remove -->
              <v-list-item
                v-if="!isDialog"
                @click.stop="removeWidget"
                prepend-icon="mdi-close-circle"
                :title="$t('widget.menu.remove')"
                data-cy="widgetMenuRemove"
              />
            </v-list>
          </v-menu>
        </span>
        <span class="widget-title-container">
          <v-tooltip
            location="bottom"
            :max-width="600"
            :disabled="!isNameOverflowing"
            :text="title"
          >
            <template v-slot:activator="{ props }">
              <div v-bind="props" class="widget-title" ref="deviceName">
                <span>{{ title }}</span>
              </div>
            </template>
          </v-tooltip>
          <div v-if="subtitle" class="widget-subtitle">
            <span>{{ subtitle }}</span>
            &nbsp;
            <span v-if="serial" class="device-serial">{{ serial }}</span>
          </div>
        </span>
        <span class="widget-left-toolbar">
          <slot name="left-toolbar"></slot>
        </span>
        <v-spacer />
        <span class="widget-right-toolbar">
          <slot name="toolbar"></slot>
        </span>
        <span v-if="fullscreen">
          <v-btn class="widget-fullscreen-button" @click="toggleFullscreen()" variant="text">
            <v-icon v-if="isFullscreen" icon="mdi-fullscreen-exit" color="grey" />
            <v-icon v-else icon="mdi-fullscreen" color="grey" />
          </v-btn>
        </span>
        <span v-if="isDialog">
          <v-btn @click="close" variant="text" data-cy="close">
            <v-icon icon="mdi-close" color="grey" />
          </v-btn>
        </span>
      </v-card-title>
    </v-card-item>
    <hr class="divider" />
    <v-card-text class="widget-content-container no-drag">
      <slot name="content"></slot>
    </v-card-text>
    <hr v-if="showFooter" class="divider" />
    <v-card-text v-if="showFooter" class="widget-footer-container">
      <slot name="footer"></slot>
    </v-card-text>
    <ConfigureDialog
      :deviceIds="deviceIds"
      :widgetId="widgetId"
      :widgetType="widgetType"
      :widgetMaxDevices="widgetMaxDevices"
      :open="dialogs.configure"
      :editing="true"
      @close="dialogs.configure = false"
      @widgetUpdated="updateWidget($event)"
      data-cy="addWidgetsDialog"
    />
  </v-card>
</template>

<script lang="ts">
import { nextTick, defineComponent, type PropType } from 'vue'
import throttle from 'lodash/throttle'
import { FusionStores } from '@fusion/saas-components-v3'
import ConfigureDialog from '@/components/dialogs/AddWidgetsDialog.vue'
import { HTTPStatus } from '@/api'
import { NAMESPACES } from '@/store'
import { DEVICE_GETTERS } from '@/store/devices'
import { getUserAccountId } from '@/helpers/loginas/logInAsHelper'
import { MessageType, SNACKBAR_STATIC_DURATION_MS } from '@/services/notifications'
import { isChildOverflowing } from '@/services/html-utilities'
import type { PatchWidgetRequest } from '@/types/dashboard'
import type { WidgetComponentKey } from '@/views/dashboard.vue'

interface WidgetDims {
  width: number
  height: number
}

interface UpdateWidgetEvent {
  widgetId: string
  deviceIds: string[]
  previousDeviceIds: string[]
}

export default defineComponent({
  name: 'BaseWidget',
  components: {
    ConfigureDialog
  },
  setup() {
    const notifications = FusionStores.notifications()
    return { notifications }
  },
  props: {
    title: {
      type: String,
      default: undefined
    },
    deviceIds: {
      type: Array as PropType<string[]>,
      default: (): string[] => []
    },
    widgetId: {
      type: String,
      default: undefined
    },
    widgetType: {
      type: String as PropType<WidgetComponentKey>,
      default: undefined
    },
    widgetMaxDevices: {
      type: Object as PropType<Record<WidgetComponentKey, number>>,
      default: undefined
    },
    subtitle: {
      type: String,
      default: undefined
    },
    fullscreen: {
      type: Boolean,
      default: false
    },
    observeSize: {
      type: Boolean,
      default: false
    },
    enableDisconnectedDeviceTheme: {
      type: Boolean,
      default: false
    },
    isDialog: {
      type: Boolean,
      default: false
    },
    showFooter: {
      type: Boolean,
      default: false
    },
    isGrayOut: {
      type: Boolean,
      default: false
    }
  },
  emits: [
    'close',
    'fullscreenChange',
    'removeDevice',
    'removeWidget',
    'refreshDashboard',
    'widgetSizeChanged'
  ],
  data() {
    return {
      accountId: null as string | null,
      isFullscreen: false,
      dialogs: { configure: false },
      isNameOverflowing: false,
      observer: null as ResizeObserver | null
    }
  },
  watch: {
    deviceIds(newVal: string[], oldVal: string[]): void {
      if (newVal.toString() !== oldVal.toString()) {
        const params: UpdateWidgetEvent = {
          widgetId: this.widgetId ?? '',
          deviceIds: newVal,
          previousDeviceIds: oldVal
        }
        this.updateWidget(params)
      }
    },
    allDevices(): void {
      this.checkDeviceAccess()
    }
  },
  computed: {
    cardTitleClasses(): string[] {
      const classes = ['widget-header-container']
      if (!this.isDialog) {
        classes.push('vue-draggable-handle')
      }
      return classes
    },
    allDevices(): string[] {
      return Object.keys(this.$store.state.devices.allDevices).filter((id) =>
        this.allowDeviceWidget(id)
      )
    },
    hasActions(): boolean {
      return !!this.$slots['actions']
    },
    hasOneDevice(): boolean {
      return !!(this.deviceIds && this.deviceIds.length && this.deviceIds.length === 1)
    },
    serial(): string {
      if (this.hasOneDevice) {
        return (
          this.$store.getters[`${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_SERIAL_BY_DEVICE_ID}`](
            this.deviceIds[0]
          ) || ''
        )
      }
      return ''
    }
  },
  methods: {
    close(): void {
      this.$emit('close')
    },
    toggleFullscreen(): void {
      this.isFullscreen = !this.isFullscreen
      this.$emit('fullscreenChange', this.isFullscreen)
    },
    removeDevice(index: number, options: unknown): void {
      this.$emit('removeDevice', index, options)
    },
    removeWidget(): void {
      this.$emit('removeWidget')
    },
    async updateWidget(event: UpdateWidgetEvent): Promise<void> {
      const { widgetId, deviceIds, previousDeviceIds } = event
      const config: PatchWidgetRequest = { deviceIds }
      if (deviceIds.length === 1) {
        const isNewDevice = !previousDeviceIds.includes(deviceIds[0])
        if (isNewDevice) {
          config.settings = {
            center: { lat: 0, lng: 0 },
            zoom: 0,
            selectedParameter: 0
          }
        }
      }
      const resp = await this.$api.patchWidget(config, widgetId)
      if (resp.ok || resp.status === HTTPStatus.NotFound) {
        this.$emit('refreshDashboard')
      } else {
        this.notifications.addMessage({
          text: this.$t('dashboard.messages.errorUpdateWidget'),
          type: MessageType.ERROR,
          timeout: SNACKBAR_STATIC_DURATION_MS
        })
      }
    },
    async setIsNameOverflowing(): Promise<void> {
      await nextTick()
      this.isNameOverflowing = isChildOverflowing(this.$refs.deviceName as HTMLElement)
    },
    allowDeviceWidget(deviceId: string): boolean {
      const isShared = this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_IS_SHARED_BY_DEVICE_ID}`
      ](deviceId) as boolean
      const isOwned =
        (this.$store.getters[`${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_ACCOUNT_BY_DEVICE_ID}`](
          deviceId
        ) as string) === this.accountId
      return isShared || isOwned
    },
    checkDeviceAccess(): void {
      if (this.allDevices.length > 0) {
        this.deviceIds?.forEach((id, index) => {
          if (!this.allDevices.includes(id)) {
            this.removeDevice(index, { force: true })
          }
        })
      }
    },
    observe(el: Element): void {
      const setDims = throttle((entries: ResizeObserverEntry[]) => {
        const cr = entries[0].contentRect
        const dims: WidgetDims = { width: cr.width, height: cr.height }
        this.$emit('widgetSizeChanged', dims)
      }, 200)

      this.observer = new ResizeObserver(setDims)
      if (el instanceof Element) {
        this.observer.observe(el)
      }
    }
  },
  async mounted() {
    if (this.observeSize) this.observe(this.$el as Element)
    this.accountId = getUserAccountId(this.$auth)
    this.checkDeviceAccess()
  },
  updated() {
    this.setIsNameOverflowing()
  },
  unmounted() {
    if (this.observer) this.observer.disconnect()
  }
})
</script>

<style lang="scss">
.gray-background-widget {
  background-color: #efefef !important;
  .aqi {
    .reading {
      background-color: grey;
    }
  }
  .inactiveTimestamp {
    color: black;
  }
}

.widget-fullscreen-button.v-btn:not(.v-btn--round).v-size--default {
  min-width: 36px;
  padding: 0;
}

.small-subtitle {
  padding: 4px 6px;
  white-space: nowrap;
  opacity: 1;

  .value {
    color: black;
    font-weight: 500;
  }

  span {
    font-size: 0.7rem;

    @media (max-width: 480px) {
      font-size: 0.675rem;
    }
  }
}
</style>
<style scoped lang="scss">
.widget {
  display: flex;
  flex-direction: column;

  &.disabled {
    opacity: 0.5;
    pointer-events: none;
  }
}
.widget-fullscreen {
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1010;
  width: 100% !important;
  height: 100% !important;
  padding: 0 !important;
  margin: 0 !important;
}

.widget-header-container,
.widget-footer-container {
  flex-wrap: nowrap;
  max-width: 100%;
  min-height: 42px;
  padding: 0 4px;
  font-size: 15px;
}

.widget-header-container {
  .widget-actions {
    margin-left: 0px;
    margin-right: 8px;
  }
  .widget-title-container {
    overflow: hidden;
    display: flex;
    flex-direction: column;
    padding-bottom: 4px;
    margin-right: 8px;
    .widget-title {
      font-size: 16px;
      margin-right: 4px;
      margin-left: 4px;
      height: 22px;
      line-height: 26px;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }
    .widget-subtitle {
      margin-left: 4px;
      height: 12px;
      display: flex;
      align-items: center;
      font-size: 12px;
      color: rgba(0, 0, 0, 0.6);

      span {
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
      }
    }
  }
  .widget-left-toolbar,
  .widget-right-toolbar {
    white-space: nowrap;
  }

  .fullscreen-button {
    padding: 0;
  }
}

.widget-footer-container {
  flex-grow: 0;
  padding: 6px 12px;
}

.v-card-title {
  display: flex;
  align-items: center;
  :deep(.v-btn) {
    padding: 0;
    min-width: 10px;
    padding-left: 0.4rem;
    padding-right: 0.4rem;

    .v-icon {
      font-size: 22px;
    }
  }
}
.widget-content-container {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 12px;
  position: relative;
}

.divider {
  opacity: 0.36;
}

.widget-disconnected-device-theme {
  border: 1px solid var(--tsi-color--accent-error);

  .widget-header-container {
    background-color: var(--tsi-color--accent-error);
    color: white !important;

    button,
    span,
    :deep(.v-icon) {
      color: white !important;
    }
  }
}
</style>
